import { Box, Collapse, IconButton, Pagination, Paper, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import { getCoreRowModel } from "@tanstack/react-table"
import type { ChangeEvent, FC } from "react"
import { useState } from "react"

import { usePendingChangesColumns } from "./hooks/usePendingChangesColumns"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { ApprovalStatus } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { PENDING_CHANGES_PAGE_SIZE } from "@/screens/Products/components/SourceChanges/constants"
import { useGetProductSourceChangeRecords } from "@/screens/Products/components/SourceChanges/hooks/useGetProductSourceChangeRecords"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface PendingChangesPanelProps {
  productId: number
}

export const PendingChangesPanel: FC<PendingChangesPanelProps> = ({ productId }) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [page, setPage] = useState(1)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const { sourceChanges, isFetching, pageCount } = useGetProductSourceChangeRecords({
    originalProductId: productId,
    approvalStatuses: [ApprovalStatus.Pending],
    offset: (page - 1) * PENDING_CHANGES_PAGE_SIZE,
    limit: PENDING_CHANGES_PAGE_SIZE,
  })

  const handleApprove = () => {
    // TODO(AK-7116): Call the approve mutation once the backend exposes it.
    notify({ type: NotificationType.Warning, message: "Approve feature coming soon" })
  }

  const handleReject = () => {
    // TODO(AK-7116): Call the reject mutation once the backend exposes it.
    notify({ type: NotificationType.Warning, message: "Reject feature coming soon" })
  }

  const handlePageChange = (_event: ChangeEvent<unknown>, value: number) => setPage(value)

  const columns = usePendingChangesColumns({ onApprove: handleApprove, onReject: handleReject })

  const hasPendingChanges = sourceChanges.length > 0

  if (!hasPendingChanges) return null

  return (
    <Paper className='bg-secondary-100 p-6' elevation={0} data-testid='source-change.pending-changes'>
      <Box className='flex items-center justify-between'>
        <Box className='flex items-center gap-x-3'>
          <WarningOutlined className='text-error-400' />
          <Typography variant='subtitle1' className='text-gray-900' data-testid='source-change.pending-changes.title'>
            Pending Changes
          </Typography>
        </Box>
        <IconButton
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "collapse" : "expand"}
          data-testid='source-change.pending-changes.toggle-button'
        >
          {isExpanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </IconButton>
      </Box>
      <Collapse in={isExpanded}>
        <Box className='pt-2'>
          <AerosBaseTable
            features={{
              table: { stickyHeader: false },
              tanstackOptions: {
                data: sourceChanges,
                columns,
                state: { isLoading: isFetching },
                getCoreRowModel: getCoreRowModel(),
                getRowId: (sourceChange) => sourceChange.id,
              },
            }}
            slotProps={{
              headerRow: { className: "hidden" },
              bodyRow: { className: "!bg-secondary-100 hover:!bg-secondary-100" },
              bodyCell: { className: "!bg-secondary-100 first:!border-l-0 last:!border-r-0" },
            }}
          />
          <Box className='flex justify-center pt-4'>
            <Pagination
              count={Math.max(pageCount, 1)}
              page={page}
              showFirstButton
              showLastButton
              color='primary'
              onChange={handlePageChange}
              data-testid='source-change.pending-changes.pagination'
            />
          </Box>
        </Box>
      </Collapse>
    </Paper>
  )
}
